import { useEffect } from 'react';

// Lightweight head-tag manager (no SSR available in this CRA app, so tags are set client-side
// on mount/update — this still lets Google and social crawlers that execute JS pick them up,
// and keeps the browser tab title / meta correct for all visitors).
const setMetaTag = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLinkTag = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const JSONLD_ID = 'seo-jsonld';

const setJsonLd = (data) => {
  let el = document.getElementById(JSONLD_ID);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = JSONLD_ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

/**
 * Seo — sets document title, meta description/robots, canonical URL,
 * Open Graph / Twitter tags, and JSON-LD structured data for the current page.
 */
const Seo = ({
  title,
  description,
  canonicalUrl,
  image,
  robots = 'index, follow',
  type = 'website',
  publishedTime,
  modifiedTime,
  jsonLd
}) => {
  useEffect(() => {
    if (title) document.title = title;

    setMetaTag('name', 'description', description);
    setMetaTag('name', 'robots', robots);

    setLinkTag('canonical', canonicalUrl);

    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:type', type);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:image', image);
    if (publishedTime) setMetaTag('property', 'article:published_time', publishedTime);
    if (modifiedTime) setMetaTag('property', 'article:modified_time', modifiedTime);

    setMetaTag('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', image);

    setJsonLd(jsonLd);

    return () => setJsonLd(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonicalUrl, image, robots, type, publishedTime, modifiedTime, jsonLd]);

  return null;
};

export default Seo;
